import { memo, useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useFormik } from 'formik';
import { NumericFormat } from 'react-number-format';
import moment from 'moment';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import FormDateField from 'components/ui/form/FormDateField';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';

const StatBlock = ({ label, value, suffix, accent }) => (
  <div className='bg-slate-50 rounded-xl p-4 border-l-4' style={{ borderLeftColor: accent }}>
    <div className='text-xs font-medium text-gray-500'>{label}</div>
    <div className='text-[20px] font-bold text-gray-800'>
      {value ? Number(Number(value).toFixed(2)) : 0} {suffix}
    </div>
  </div>
);

const ProfileStats = () => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();

  const formik = useFormik({
    initialValues: { date: moment().format('YYYY-MM-DD') },
    onSubmit: () => {},
  });

  const date = formik.values.date;

  // Profile + hr info
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['/admin/profile/edit'],
    queryFn: async () => {
      const res = await axiosPrivate.get('/admin/profile/edit');
      return res?.data?.data;
    },
    retry: false,
  });

  const hrId = profile?.hr?.object_id;
  const userId = profile?.id;

  // Worked time data
  const { data: hrData, isLoading: hrLoading } = useQuery({
    queryKey: ['/hr/onlyuser', hrId, date],
    queryFn: async () => {
      const res = await axiosPrivate.get(`/hr/onlyuser/${hrId}?date=${date}`);
      return res?.data?.data;
    },
    enabled: !!hrId && !!date,
    retry: false,
  });

  // KPI data
  const [kpi, setKpi] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);

  useEffect(() => {
    if (!userId || !date) return;
    const m = moment(date, 'YYYY-MM-DD');
    const from = m.clone().startOf('month').format('YYYY-MM-DD');
    const till = m.clone().endOf('month').format('YYYY-MM-DD');
    setKpiLoading(true);
    axiosPrivate
      .get(`/admin/staff-kpi/staff/${userId}?from=${from}&till=${till}`)
      .then((res) => setKpi(res?.data?.data || null))
      .catch(() => setKpi(null))
      .finally(() => setKpiLoading(false));
  }, [userId, date, axiosPrivate]);

  // /hr/onlyuser response: stats / salary / valute are at top level (no report wrapper)
  const stats = hrData?.stats;
  const salary = hrData?.salary;
  const valute = hrData?.valute;
  const percent = useMemo(() => {
    if (!stats?.planned_minutes) return 0;
    return (stats.total_minutes / stats.planned_minutes) * 100;
  }, [stats]);

  if (profileLoading) {
    return (
      <div className='flex justify-center py-12'>
        <CircularProgress size={32} />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      {/* Date filter */}
      <div className='bg-white base-border p-4 flex items-center gap-2'>
        <div className='flex-1'>
          <FormDateField
            delay={0}
            label={t('common.fields.date')}
            fieldName='date'
            formik={formik}
          />
        </div>
        <Button
          variant='outlined'
          size='small'
          onClick={() =>
            formik.setFieldValue('date', moment().format('YYYY-MM-DD'))
          }
        >
          Bugun
        </Button>
      </div>

      {/* Worked time */}
      <div className='bg-white base-border p-5'>
        <div className='text-base font-semibold mb-3 flex items-center gap-2'>
          <i className='bi bi-clock-history text-blue-500' />
          Ish vaqti
        </div>

        {!hrId ? (
          <div className='bg-amber-50 border border-amber-200 rounded-xl p-4 text-center text-amber-700 text-sm'>
            <i className='bi bi-exclamation-triangle mr-1' />
            {t('hr.connect.notConnected')}
          </div>
        ) : hrLoading ? (
          <div className='flex justify-center py-6'>
            <CircularProgress size={22} />
          </div>
        ) : stats ? (
          <>
            <div className='grid grid-cols-2 gap-2 mb-4'>
              <StatBlock
                label={t('boss.offices.workStatuses.total_minutes')}
                value={stats.total_minutes}
                suffix={t('common.fields.mins')}
                accent='#22c55e'
              />
              <StatBlock
                label={t('boss.offices.workStatuses.planned_minutes')}
                value={stats.planned_minutes}
                suffix={t('common.fields.mins')}
                accent='#6366f1'
              />
              <StatBlock
                label={t('boss.offices.workStatuses.overtime_minutes')}
                value={stats.overtime_minutes}
                suffix={t('common.fields.mins')}
                accent='#f59e0b'
              />
              <StatBlock
                label={t('boss.offices.workStatuses.under_minutes')}
                value={stats.under_minutes}
                suffix={t('common.fields.mins')}
                accent='#ef4444'
              />
            </div>

            {/* Salary progress */}
            <div className='flex items-center gap-2'>
              <div>0</div>
              <div className='bg-blue-100 h-3 grow rounded-full'>
                <div
                  style={{ width: `${Math.min(percent, 100)}%` }}
                  className='bg-blue-600 h-3 rounded-full'
                />
              </div>
              <div className='whitespace-nowrap flex flex-col items-end leading-tight'>
                <span className='text-green-600 font-semibold text-sm'>
                  <CurrencyFormat
                    value={Number(
                      (percent * (salary || 0)) / 100,
                    )}
                    suffix={` ${valute || ''}`}
                    decimalScale={2}
                  />
                </span>
                <span className='text-gray-500 text-xs'>
                  /{' '}
                  <CurrencyFormat
                    value={salary}
                    suffix={` ${valute || ''}`}
                    decimalScale={2}
                  />
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className='text-center text-gray-400 py-4 text-sm'>
            <i className='bi bi-exclamation-octagon mr-1' />
            {t('common.global.noDataFound')}
          </div>
        )}
      </div>

      {/* KPI */}
      <div className='bg-white base-border p-5'>
        <div className='text-base font-semibold mb-3 flex items-center gap-2'>
          <i className='bi bi-cash-coin text-green-500' />
          {t('settings.staffKpi.earnings.title')}
        </div>

        {kpiLoading ? (
          <div className='flex justify-center py-6'>
            <CircularProgress size={22} />
          </div>
        ) : kpi?.items?.length > 0 ? (
          <div className='flex flex-col gap-2'>
            {kpi.items.map((item, idx) => (
              <div
                key={idx}
                className='flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 bg-white gap-2'
              >
                <div className='flex flex-col gap-0.5 min-w-0 flex-1'>
                  <span className='font-medium text-sm text-gray-800 truncate'>
                    {item?.kpi?.type?.name || `#${item?.kpi?.type_id}`}
                  </span>
                  <span className='text-xs text-gray-500 flex items-center gap-1'>
                    {item?.kpi?.count_type_text} ·
                    <NumericFormat
                      value={item?.kpi?.price}
                      displayType='text'
                      thousandSeparator={item?.kpi?.count_type === 2 ? ' ' : ''}
                      decimalScale={2}
                    />
                    {item?.kpi?.count_type === 1 ? '%' : ''}
                  </span>
                </div>
                <div className='flex flex-col items-end text-sm font-medium shrink-0'>
                  {item?.earned_sum > 0 && (
                    <NumericFormat
                      value={item.earned_sum}
                      displayType='text'
                      thousandSeparator=' '
                      decimalScale={2}
                      suffix=' UZS'
                    />
                  )}
                  {item?.earned_valute > 0 && (
                    <NumericFormat
                      value={item.earned_valute}
                      displayType='text'
                      thousandSeparator=' '
                      decimalScale={2}
                      suffix=' $'
                    />
                  )}
                  {!item?.earned_sum && !item?.earned_valute && (
                    <span className='text-gray-400'>—</span>
                  )}
                </div>
              </div>
            ))}
            <div className='flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 mt-1'>
              <span className='font-medium text-emerald-700 text-sm'>
                {t('common.table.total')}:
              </span>
              <div className='flex flex-col items-end font-semibold text-emerald-700 text-sm'>
                {kpi?.total_sum > 0 && (
                  <NumericFormat
                    value={kpi.total_sum}
                    displayType='text'
                    thousandSeparator=' '
                    decimalScale={2}
                    suffix=' UZS'
                  />
                )}
                {kpi?.total_valute > 0 && (
                  <NumericFormat
                    value={kpi.total_valute}
                    displayType='text'
                    thousandSeparator=' '
                    decimalScale={2}
                    suffix=' $'
                  />
                )}
                {!kpi?.total_sum && !kpi?.total_valute && <span>0 UZS</span>}
              </div>
            </div>
          </div>
        ) : (
          <div className='text-center text-gray-400 py-4 text-sm'>
            <i className='bi bi-exclamation-octagon mr-1' />
            {t('common.global.noDataFound')}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(ProfileStats);
